import React, { useState, useEffect } from 'react';
import { collection, query, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, onSnapshot, arrayUnion, arrayRemove, increment } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Heart, Plus, Search, ChevronLeft, Trash2 } from 'lucide-react';
import PageHeader from './common/PageHeader';
import { isAdminUser } from '../utils/adminClaim';

const CATEGORIES = ['소통 문제', '작업 기준', '납기/일정', '비용/단가', '품질 기대치', '기타'];

function getRoleBadge(businessType) {
    if (!businessType) return '회원';
    const t = businessType.toLowerCase();
    if (t === 'dental' || t === 'clinic' || t === '치과') return '치과';
    if (t === 'lab' || t === '기공소') return '기공소';
    return '회원';
}

function getRoleBadgeStyle(role) {
    switch (role) {
        case '치과': return { background: '#dbeafe', color: '#1d4ed8' };
        case '기공소': return { background: '#fef3c7', color: '#92400e' };
        default: return { background: '#f3e8ff', color: '#7c3aed' };
    }
}

function EmpathyBoard({ user }) {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [selectedPostId, setSelectedPostId] = useState(null);
    const [formData, setFormData] = useState({ category: '소통 문제', content: '' });
    const [submitting, setSubmitting] = useState(false);

    // 실시간 게시물 로드
    useEffect(() => {
        const q = query(collection(db, 'empathyPosts'));
        const unsub = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setPosts(data);
            setLoading(false);
        }, (error) => {
            console.error('empathyPosts 로드 오류:', error);
            setLoading(false);
        });
        return () => unsub();
    }, []);

    // 필터링
    const filtered = posts.filter(p => {
        if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            return (p.content || '').toLowerCase().includes(s) || (p.category || '').toLowerCase().includes(s);
        }
        return true;
    });

    // 글 작성
    const handleSubmit = async () => {
        if (!formData.content.trim()) { alert('내용을 입력해주세요.'); return; }
        setSubmitting(true);
        try {
            await addDoc(collection(db, 'empathyPosts'), {
                category: formData.category,
                content: formData.content.trim(),
                authorId: user.uid,
                authorRole: getRoleBadge(user.businessType || user.companyBusinessType),
                empathyCount: 0,
                empathyUsers: [],
                status: 'empathy',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            setFormData({ category: '소통 문제', content: '' });
            setShowForm(false);
        } catch (e) { alert('등록 실패: ' + e.message); }
        setSubmitting(false);
    };

    // 공감 토글
    const handleEmpathy = async (post) => {
        if (!user?.uid) return;
        const postRef = doc(db, 'empathyPosts', post.id);
        const alreadyEmpathized = (post.empathyUsers || []).includes(user.uid);

        if (alreadyEmpathized) {
            await updateDoc(postRef, {
                empathyUsers: arrayRemove(user.uid),
                empathyCount: increment(-1),
                updatedAt: serverTimestamp(),
            });
        } else {
            await updateDoc(postRef, {
                empathyUsers: arrayUnion(user.uid),
                empathyCount: increment(1),
                updatedAt: serverTimestamp(),
            });
        }
    };

    // 삭제
    const handleDelete = async (postId) => {
        if (!window.confirm('게시물을 삭제하시겠습니까?')) return;
        await deleteDoc(doc(db, 'empathyPosts', postId));
        setSelectedPostId(null);
    };

    // posts에서 최신 데이터로 selectedPost 참조
    const selectedPost = selectedPostId ? posts.find(p => p.id === selectedPostId) : null;

    const fmtDate = (ts) => {
        if (!ts) return '';
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return d.toLocaleDateString('ko-KR');
    };

    if (loading) return <div style={S.loading}>로딩 중...</div>;

    // 상세 화면
    if (selectedPost) {
        return (
            <div style={S.container}>
                <button onClick={() => setSelectedPostId(null)} style={S.backBtn}><ChevronLeft size={18} /> 목록으로</button>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ ...S.categoryChip, background: '#f3f4f6', color: '#374151' }}>{selectedPost.category}</span>
                </div>

                <p style={S.detailContent}>{selectedPost.content}</p>

                <div style={S.authorRow}>
                    <span style={{ ...S.roleBadgeSmall, ...getRoleBadgeStyle(selectedPost.authorRole) }}>{selectedPost.authorRole}</span>
                    <span style={S.dateText}>{fmtDate(selectedPost.createdAt)}</span>
                    <span style={{ color: '#ef4444', fontWeight: 600 }}>❤️ {selectedPost.empathyCount || 0}</span>
                    {(selectedPost.authorId === user?.uid || isAdminUser(user)) && (
                        <button onClick={() => handleDelete(selectedPost.id)} style={S.deleteBtn}><Trash2 size={14} /> 삭제</button>
                    )}
                </div>

                <button onClick={() => handleEmpathy(selectedPost)} style={{
                    ...S.empathyBtn,
                    ...(selectedPost.empathyUsers?.includes(user?.uid) ? S.empathyBtnActive : {})
                }}>
                    <Heart size={16} fill={selectedPost.empathyUsers?.includes(user?.uid) ? '#fff' : 'none'} />
                    공감 {selectedPost.empathyCount || 0}
                </button>
            </div>
        );
    }

    // 메인 목록
    return (
        <div style={S.container}>
            <PageHeader
                title="공감마당"
                actions={
                    <button onClick={() => setShowForm(true)} style={S.createBtn}><Plus size={18} /> 이슈 등록</button>
                }
            >
                <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                    의사·기공사·위생사 간 소통과 화합을 위한 공간
                </p>
            </PageHeader>

            {/* 카테고리 필터 + 검색 */}
            <div style={S.toolbar}>
                <div style={S.filterRow}>
                    <button onClick={() => setCategoryFilter('all')}
                        style={{ ...S.filterBtn, ...(categoryFilter === 'all' ? S.filterActive : {}) }}>전체</button>
                    {CATEGORIES.map(c => (
                        <button key={c} onClick={() => setCategoryFilter(c)}
                            style={{ ...S.filterBtn, ...(categoryFilter === c ? S.filterActive : {}) }}>{c}</button>
                    ))}
                </div>
                <div style={S.searchBox}>
                    <Search size={16} color="#9ca3af" />
                    <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                        placeholder="검색" style={S.searchInput} />
                </div>
            </div>

            {/* 글쓰기 모달 */}
            {showForm && (
                <div style={S.modal} onClick={() => setShowForm(false)}>
                    <div style={S.modalContent} onClick={e => e.stopPropagation()}>
                        <h2 style={{ margin: '0 0 16px' }}>이슈 등록</h2>
                        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280' }}>
                            익명으로 등록됩니다. 직군(치과/기공소/회원)만 표시됩니다.
                        </p>
                        <label style={S.label}>카테고리</label>
                        <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} style={S.input}>
                            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <label style={S.label}>내용</label>
                        <textarea value={formData.content} onChange={e => setFormData({ ...formData, content: e.target.value })}
                            placeholder="겪고 있는 이슈나 불편사항을 자유롭게 적어주세요" rows={5} style={{ ...S.input, resize: 'vertical' }} />
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowForm(false)} style={S.cancelBtn}>취소</button>
                            <button onClick={handleSubmit} disabled={submitting} style={S.submitBtn}>{submitting ? '등록 중...' : '등록'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 목록 */}
            {filtered.length === 0 ? (
                <div style={S.empty}>
                    <Heart size={48} color="#d1d5db" />
                    <p>등록된 이슈가 없습니다</p>
                </div>
            ) : (
                <div style={S.list}>
                    {filtered.map(p => (
                        <div key={p.id} style={S.card} onClick={() => setSelectedPostId(p.id)}>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                <span style={S.categoryChip}>{p.category}</span>
                            </div>
                            <p style={S.cardContent}>{(p.content || '').slice(0, 120)}</p>
                            <div style={S.cardMeta}>
                                <span style={{ ...S.roleBadgeSmall, ...getRoleBadgeStyle(p.authorRole) }}>{p.authorRole}</span>
                                <span>{fmtDate(p.createdAt)}</span>
                                <span style={{ color: '#ef4444' }}>❤️ {p.empathyCount || 0}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const S = {
    container: { maxWidth: 800, margin: '0 auto', padding: 24 },
    loading: { textAlign: 'center', padding: 48, color: '#6b7280' },
    toolbar: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' },
    filterRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
    filterBtn: { padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: 20, background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 500 },
    filterActive: { background: '#fef3c7', borderColor: '#f59e0b', color: '#92400e' },
    searchBox: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, flex: 1, minWidth: 150 },
    searchInput: { border: 'none', outline: 'none', flex: 1, fontSize: 13 },
    createBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 },
    list: { display: 'flex', flexDirection: 'column', gap: 12 },
    card: { padding: 16, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, cursor: 'pointer', transition: 'box-shadow 0.2s' },
    categoryChip: { display: 'inline-block', padding: '3px 10px', background: '#f3f4f6', borderRadius: 12, fontSize: 11, fontWeight: 600, color: '#4b5563' },
    cardContent: { margin: '0 0 8px', fontSize: 14, color: '#374151', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
    cardMeta: { display: 'flex', gap: 10, fontSize: 12, color: '#9ca3af', alignItems: 'center' },
    roleBadgeSmall: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 },
    empty: { textAlign: 'center', padding: 48, color: '#9ca3af' },
    modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { background: '#fff', borderRadius: 16, padding: 24, width: '90%', maxWidth: 500 },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' },
    label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 },
    cancelBtn: { padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: 'pointer' },
    submitBtn: { padding: '8px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
    backBtn: { display: 'flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280', marginBottom: 16, fontSize: 14 },
    detailContent: { whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: 15, margin: '0 0 16px' },
    authorRow: { display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, color: '#6b7280', marginBottom: 16, flexWrap: 'wrap' },
    dateText: { color: '#9ca3af' },
    deleteBtn: { display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto', padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
    empathyBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', border: '1px solid #fca5a5', borderRadius: 20, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#ef4444' },
    empathyBtnActive: { background: '#ef4444', color: '#fff', borderColor: '#ef4444' },
};

export default EmpathyBoard;
